import { RawEvent } from '@fullstory/recording-api';
import {
  NodePath,
  ObjectQueryOpts,
  ObjectInfo,
  ObjectPathPattern,
  ObjectPlatform,
  ObjectSelectorPattern,
  PlaybackContext,
  PlayPageOptions,
  PlayResult,
  Renderer,
  RendererCursor,
  RendererHost,
  ROBOTO_CSS,
  SeekResult,
  ViewPath,
  ObjectCollection,
  GroupedObjectInfo,
} from '@fullstory/renderer-shared';
import { FramePlayback, FramePlaybackHost, LoadResult } from '@fullstory/playback-web';
import { DetectedMobileFramework, PageDesc, PageTypeEnum } from '@fullstory/client-api';
import { AbstractRendererImpl } from '../abstract_renderer';
import { OffsetRect, Size } from '@fullstory/shared/viewport';
import {
  AndroidDriver,
  CanvasRenderer,
  DataUnpack,
  DebugSocket,
  Frame,
  Interpolation,
  Interpolator,
  IOSDriver,
  Matrix,
  MobileDriver,
  MobileMockDriver,
  MobilePageData,
  MobileTypeEnum,
  ObjectId,
  ObjectIdMap,
  Resources,
  View,
  ViewMatch,
  WebBundle,
  WebBundlesBuilder,
  WebViewsState,
  logWarnOnce,
} from '@fullstory/playback-mobile';
import { MobileOverlays } from './mobileoverlay';
import { OverlayMode, Overlays } from '../overlays/overlay';
import { CreateClient, Dispatcher } from '@fullstory/channel/proxy';
import { InspectorHostClientStub } from '../page-insights/inspector';
import { MobileInspector } from './page-insights/inspector';
import { FrameBundleRootProto } from '@fullstory/mobile-flatbuffers';
import { cacheAndReturn } from '@fullstory/util/cache';
import { loadEvent } from '@fullstory/util/promise';
import { Point, topLeft, transformRect } from '@fullstory/util/geometry';
import * as flatbuffers from 'flatbuffers';
import { PiecewiseFunction } from '@fullstory/piecewise-function';
import { ActivityMapsHostClientStub } from '../page-insights/activitymaps';
import { MobileActivityMaps } from './page-insights/activitymaps';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';
import { assertExhaustive } from '@fullstory/typeutils';
import { WebInspector } from '../web/page-insights/inspector';
import { parseSelector } from '@fullstory/selectors';
import { NormalizedPageDesc } from '@fullstory/playback-model';

type WebView = {
  container: HTMLDivElement;
  epoch: number;
  playback: FramePlayback;
  inspector: WebInspector;
};

export class MobilePageRenderer extends AbstractRendererImpl implements Renderer {
  private _page: Readonly<
    Pick<NormalizedPageDesc, 'BuildId' | 'GcsBucketUrl' | 'Type' | 'RawEventsUrls' | 'AppData'>
  >;
  private _renderer: CanvasRenderer;
  private _pageData: MobilePageData;
  private _nextFrameIndex = 0; /* We always render an interpolation of two frames; this is the index of the second one in _pageData.frames */
  private _curInterpolation: Interpolation;
  private _canvas: HTMLCanvasElement;
  private _webViews: ObjectIdMap<WebView> = {};
  private _renderTimer: any;
  private _skippedFrames: number;
  private _debugSocket: DebugSocket;
  private _keyboardOpenTimeline: PiecewiseFunction<boolean>;
  private _scale: number;
  // The timestamp of the last frame from all bundles loaded.
  private _lastFrameTime: number;
  private readonly _windowSize: Readonly<Size>;
  private readonly _screenSize: Readonly<Size>;
  private readonly _ROBOTO_CSS_loaded: Promise<void>;
  private _protocolVersion: number;

  /** Cached bounding rects for views. */
  #boundingRectCache: Map<ObjectId, DOMRect> = new Map();
  /** A cached transform from the canvas's coordinate system to the renderer iframe. */
  #fromRendererFrameMatrixCache: DOMMatrixReadOnly | null = null;
  /** A cached transform from the renderer iframe to the canvas's coordinate system. */
  #toRendererFrameMatrixCache: DOMMatrixReadOnly | null = null;
  /** A cache mapping selectors to the views they match. */
  #querySelectorAllCache: Map<string, ViewMatch[]> = new Map();

  constructor(host: RendererHost, dispatcher: Dispatcher, ctx: PlaybackContext) {
    super(host, dispatcher, ctx);

    this._windowSize = {
      width: ctx.page.windowWidth,
      height: ctx.page.windowHeight,
    };
    this._screenSize = {
      width: ctx.page.screenWidth,
      height: ctx.page.screenHeight,
    };

    // TODO(mmastrac): Temporary - this should be loaded as part of the page CSS
    const robotoCssLinkElem = document.createElement('link');
    robotoCssLinkElem.rel = 'stylesheet';
    robotoCssLinkElem.type = 'text/css';
    robotoCssLinkElem.href = ROBOTO_CSS;
    this._host.onResourceEvent({
      kind: 'resourceLoadStart',
      href: ROBOTO_CSS,
      destination: 'style',
    });

    this._ROBOTO_CSS_loaded = (async () => {
      try {
        await loadEvent(robotoCssLinkElem, () => {
          document.body.appendChild(robotoCssLinkElem);
        });
      } catch (_) {
        this._host.onResourceEvent({
          kind: 'resourceLoadFailed',
          href: ROBOTO_CSS,
          destination: 'style',
        });
      }
    })();

    let removePreviousPixelRatioListener = null;

    // See https://developer.mozilla.org/en-US/docs/Web/API/Window/devicePixelRatio#monitoring_screen_resolution_or_zoom_level_changes
    const updateRendererScaleForPixelRatio = () => {
      if (removePreviousPixelRatioListener != null) {
        removePreviousPixelRatioListener();
      }
      const pixelRatioQuery = `(resolution: ${window.devicePixelRatio}dppx)`;
      const screen = matchMedia(pixelRatioQuery);
      screen.addEventListener('change', updateRendererScaleForPixelRatio);
      removePreviousPixelRatioListener = () => {
        screen.removeEventListener('change', updateRendererScaleForPixelRatio);
      };
      this._scale = window.devicePixelRatio || 1;
      // Canvas will be updated in the next played frame (prevents jumping and other rendering glitches)
    };

    // Create the canvas we'll use for rendering and insert it into the top
    // level playback container.
    const container = this.playbackContainer().elem();
    const containerDocument = container.ownerDocument || document;
    this._scale = window.devicePixelRatio || 1;
    this._canvas = containerDocument.createElement('canvas');
    this._canvas.width = this._screenSize.width * this._scale;
    this._canvas.height = this._screenSize.height * this._scale;
    this._canvas.style.position = 'relative'; // ensure it renders on top of webviews, which are position:absolute, while also receiving click events in inspect mode
    container.appendChild(this._canvas);
    this.resize();
    updateRendererScaleForPixelRatio();

    this._skippedFrames = 0;
    this._lastFrameTime = 0;
  }

  protected createOverlays(): Overlays {
    return new MobileOverlays(this);
  }

  getCurrentFrame(): Frame {
    // we're usually interpolating between two frames (_nextFrameIndex>1); in that case return the first of the two
    // if _nextFrameIndex is 0, then it's either a 1-frame session or we're not fully initialized yet, so return frame 0
    const index = this._nextFrameIndex > 1 ? this._nextFrameIndex - 1 : 0;
    return this._pageData.frames[index] || null;
  }

  getWebViews(): WebView[] {
    return Object.values(this._webViews);
  }

  async hasWebViewAt(
    offsetMs: number,
    opts: { onlyIncludeRecorded?: boolean } = {},
  ): Promise<boolean> {
    const webViewsState = this._pageData.webBundles.computeState(offsetMs);
    for (const webView in this._webViews) {
      if (webViewsState.existsNow(webView, this._webViews[webView].epoch)) {
        if (opts.onlyIncludeRecorded) {
          if (webViewsState.views[webView].recordingAllowed) {
            return true;
          }
        } else {
          return true;
        }
      }
    }

    return false;
  }

  getObjectPlatform(): ObjectPlatform {
    const nativePlatform = this.getNativePlatform();
    switch (nativePlatform) {
      case NativePlatform.NATIVE_PLATFORM_ANDROID:
        return MobileTypeEnum.Android;
      case NativePlatform.NATIVE_PLATFORM_INVALID:
        return MobileTypeEnum.Android; // Arbitrarily chosen.
      case NativePlatform.NATIVE_PLATFORM_IOS:
        return MobileTypeEnum.iOS;
      case NativePlatform.NATIVE_PLATFORM_WEB:
        throw new Error(`Unexpected platform '${nativePlatform}' for mobile renderer`);
      default:
        assertExhaustive(nativePlatform);
    }
  }

  getNativePlatform(): NativePlatform {
    const pageType = this._ctx.page.pageType || this._pageData.rawResourceData.type;
    switch (pageType) {
      case MobileTypeEnum.Android:
        return NativePlatform.NATIVE_PLATFORM_ANDROID;
      case MobileTypeEnum.iOS:
        return NativePlatform.NATIVE_PLATFORM_IOS;
      case MobileTypeEnum.MobileMock:
        return NativePlatform.NATIVE_PLATFORM_INVALID;
      default:
        throw new Error(`Unexpected page type '${pageType}' for mobile renderer`);
    }
  }

  canvasRenderer(): CanvasRenderer {
    return this._renderer;
  }

  teardown(): void {}

  createInspector(): OverlayMode {
    const inspectorHost = CreateClient(
      this._dispatcher.Ch(),
      new InspectorHostClientStub(),
      'InspectorHost',
    );
    const inspectorImpl = new MobileInspector(this, inspectorHost, this._debugSocket);

    /* 1.20.0 on iOS has date Oct 27 2021.  But protocolVersion is
     * localized to device time, not GMT!!  So we pick Oct 25 2021 as the
     * threshold for iOS plugin 1.20.0.  */
    inspectorImpl.hasSwiftUIMetaSelectorSupport = this._protocolVersion > 1635145200;
    inspectorImpl.hasSwiftUIStableSelectorsAvailable =
      this._pageData.rawResourceData &&
      this._pageData.rawResourceData.fsConfig &&
      this._pageData.rawResourceData.fsConfig.SwiftUISelectorVersion !== null;
    inspectorImpl.hasOptedIntoSwiftUIStableSelectors =
      this._pageData.rawResourceData &&
      this._pageData.rawResourceData.fsConfig &&
      this._pageData.rawResourceData.fsConfig.SwiftUISelectorVersion > 0;
    inspectorImpl.hasComposeStableSelectorsAvailable =
      this._pageData.rawResourceData &&
      this._pageData.rawResourceData.fsConfig &&
      this._pageData.rawResourceData.fsConfig.composeSelectorVersion !== null;
    inspectorImpl.hasOptedIntoComposeStableSelectors =
      this._pageData.rawResourceData &&
      this._pageData.rawResourceData.fsConfig &&
      this._pageData.rawResourceData.fsConfig.composeSelectorVersion > 0;
    inspectorImpl.missingFullStoryCommandLine =
      this._pageData.rawResourceData &&
      !this._pageData.rawResourceData.fsConfig &&
      this._pageData.rawResourceData.type === MobileTypeEnum.iOS;
    inspectorImpl.isReactNative =
      this._page.AppData?.DetectedMobileFrameworks &&
      this._page.AppData?.DetectedMobileFrameworks.includes(DetectedMobileFramework.REACT_NATIVE);
    this._dispatcher.RegisterService(inspectorImpl, 'Inspector');
    return inspectorImpl;
  }

  createActivityMaps(): OverlayMode {
    const activityMapsHost = CreateClient(
      this._dispatcher.Ch(),
      new ActivityMapsHostClientStub(),
      'ActivityMapsHost',
    );
    const activityMapsImpl = new MobileActivityMaps(this, activityMapsHost);
    this._dispatcher.RegisterService(activityMapsImpl, 'ActivityMaps');
    return activityMapsImpl;
  }

  override queryObjectsAtPoint(rendererFramePoint: Point): ObjectInfo[] {
    const canvasPoint = this.fromRendererFrameMatrix.transformPoint(rendererFramePoint);
    const matches = this._renderer?.viewsAtPoint(canvasPoint) ?? [];

    const results: ObjectInfo[] = [];
    for (const match of matches) {
      if (match.view.isWebView) {
        results.push(...this.#queryObjectsAtWebViewPoint(match.view.uniqueId, rendererFramePoint));
      }
      results.push(this.#summarizeMatch(match));
    }

    return results;
  }

  #queryObjectsAtWebViewPoint(viewId: ObjectId, rendererFramePoint: Point): ObjectInfo[] {
    const webView = this._webViews[viewId];
    if (!webView) {
      return [];
    }
    if (!this._renderer?.webViewState()?.existsNow(viewId, webView.epoch)) {
      return [];
    }
    return webView.playback
      .queryPoint(rendererFramePoint)
      .map(match => this.#summarizeWebViewMatch(viewId, match));
  }

  override queryPathPattern(pattern: ObjectPathPattern): ObjectInfo | undefined {
    const viewPath = ViewPath.fromString(pattern.path);
    if (!viewPath) {
      return undefined;
    }
    return this.#queryPath(viewPath, pattern.opts);
  }

  #queryPath(path: ViewPath, opts?: ObjectQueryOpts): ObjectInfo | undefined {
    if (path.isWebViewPath()) {
      const webView = this._webViews[path.viewId];
      if (!webView) {
        return undefined;
      }
      if (!this._renderer?.webViewState()?.existsNow(path.viewId, webView.epoch)) {
        return undefined;
      }
      return this.#summarizeWebViewMatch(
        path.viewId,
        webView.playback.queryPath(path.webViewNodePath, opts),
      );
    }

    const match = this._renderer?.queryView(path.viewId);
    return match ? this.#summarizeMatch(match, opts?.includeInnerText) : undefined;
  }

  override querySelectorPattern(pattern: ObjectSelectorPattern): ObjectInfo[] {
    const matches: ObjectInfo[] = [];

    if (!pattern.platforms || pattern.platforms.includes(this.getObjectPlatform())) {
      matches.push(
        ...this.#querySelector(pattern.selector).map(match =>
          this.#summarizeMatch(match, pattern.opts?.includeInnerText),
        ),
      );
    }

    if (!pattern.platforms || pattern.platforms.includes('web')) {
      for (const [viewId, webView] of Object.entries(this._webViews)) {
        matches.push(
          ...webView.playback
            .querySelector(pattern.selector)
            .map(match => this.#summarizeWebViewMatch(viewId, match)),
        );
      }
    }

    return matches;
  }

  #querySelector(selector: string): ViewMatch[] {
    const cachedMatches = this.#querySelectorAllCache.get(selector);
    if (cachedMatches) {
      return cachedMatches;
    }

    try {
      const matches = this._renderer?.querySelectorAll(parseSelector(selector)) ?? [];
      return cacheAndReturn(this.#querySelectorAllCache, selector, matches);
    } catch (e) {
      console.log('#queryViewsForSelector for', selector, 'failed:', e);
      return cacheAndReturn(this.#querySelectorAllCache, selector, []);
    }
  }

  isDescendant(testPath: string, basePath: string): boolean {
    const testViewPath = ViewPath.fromString(testPath);
    const baseViewPath = ViewPath.fromString(basePath);
    if (
      !testViewPath ||
      !baseViewPath ||
      testViewPath.isWebViewPath() !== baseViewPath.isWebViewPath()
    ) {
      return false;
    }
    // if in web view, we can just use the underlying node path method directly
    if (testViewPath.isWebViewPath()) {
      const testWebView = this._webViews[testViewPath.viewId];
      const baseWebView = this._webViews[baseViewPath.viewId];
      if (!testWebView || testWebView !== baseWebView) {
        return false;
      }
      if (!this._renderer?.webViewState()?.existsNow(testViewPath.viewId, testWebView.epoch)) {
        return false;
      }
      return testWebView.playback.isDescendant(
        testViewPath.webViewNodePath,
        baseViewPath.webViewNodePath,
      );
    }
    // otherwise, let's grab the native views and check from there
    const testViewMatch = this._renderer?.queryView(testViewPath.viewId);
    const baseViewMatch = this._renderer?.queryView(baseViewPath.viewId);
    if (!testViewMatch || !baseViewMatch) {
      return false;
    }
    return testViewMatch.view.isDescendant(baseViewMatch.view);
  }

  computeDescendants(collection: ObjectCollection) {
    const byWebView = new Map<
      WebView,
      { [path: string]: GroupedObjectInfo & { originalPath: string } }
    >();
    const native: ObjectCollection = {};
    Object.entries(collection).forEach(([path, object]) => {
      const viewPath = ViewPath.fromString(path);
      if (!viewPath) {
        return;
      }
      if (viewPath.webViewNodePath) {
        const webView = this._webViews[viewPath.viewId];
        if (this._renderer?.webViewState()?.existsNow(viewPath.viewId, webView.epoch)) {
          const webViewCollection = byWebView.get(webView) ?? {};
          // taking a copy here and ensuring descendants are cleared out
          // we will later map the paths back to the original non web view paths
          webViewCollection[viewPath.webViewNodePath.asString] = {
            ...object,
            originalPath: object.path,
            path: viewPath.webViewNodePath.asString,
            descendants: undefined,
          };
          byWebView.set(webView, webViewCollection);
        }
      } else {
        native[path] = object;
      }
    });
    // handle each web view separately as hierarchy is specific to each independently
    // this modifies object copies, we need to map the web view objects back on to the original
    byWebView.forEach((webViewCollection, webView) => {
      webView.playback.computeDescendants(webViewCollection);
      Object.values(webViewCollection).forEach(object => {
        const path = object.originalPath;
        collection[path].descendants = object.descendants?.map(descendant => ({
          path: webViewCollection[descendant.path].originalPath,
          depth: descendant.depth,
        }));
      });
    });
    // then handle native which has its own hierarchy
    // this modifies the objects directly
    this._renderer?.computeDescendants(native);
  }

  #summarizeMatch = (match: ViewMatch, includeInnerText?: boolean): ObjectInfo => ({
    path: match.path.toString(),
    platform: this.getObjectPlatform(),
    rect: this.getRendererFrameBoundingRectFor(match.view),
    selector: match.selector(),
    innerText: includeInnerText ? match.view.innerTextRecursive() : undefined,
  });

  #summarizeWebViewMatch(webViewId: ObjectId, match: ObjectInfo): ObjectInfo {
    const nodePath = NodePath.fromString(match.path);
    if (!nodePath) {
      console.log('Invalid NodePath in WebView match', match.path);
      return match;
    }

    return {
      ...match,
      path: new ViewPath(webViewId, nodePath).toString(),
    };
  }

  /**
   * @returns the transform from the canvas's coordinate system to the renderer
   * frame.
   */
  get toRendererFrameMatrix(): DOMMatrixReadOnly {
    if (this.#toRendererFrameMatrixCache) {
      return this.#toRendererFrameMatrixCache;
    }

    const canvasElementBounds = this._canvas.getBoundingClientRect();
    const canvasContentWidth = this._canvas.width;
    if (canvasContentWidth <= 0) {
      return (this.#toRendererFrameMatrixCache = new DOMMatrix());
    }

    // (0, 0) in the canvas content's coordinate system maps to the top-left
    // corner of the canvas element.
    const translation = topLeft(canvasElementBounds);

    // The scale factor is the ratio of the width of the canvas element to the
    // width of the canvas content.
    const scale = canvasElementBounds.width / canvasContentWidth;

    const toRendererFrameMatrix = new DOMMatrix();
    toRendererFrameMatrix.translateSelf(translation.x, translation.y);
    toRendererFrameMatrix.scaleSelf(scale);
    return (this.#toRendererFrameMatrixCache = toRendererFrameMatrix);
  }

  /**
   * @returns the transform from the renderer frame's coordinate system to the
   * canvas.
   */
  get fromRendererFrameMatrix(): DOMMatrixReadOnly {
    if (this.#fromRendererFrameMatrixCache) {
      return this.#fromRendererFrameMatrixCache;
    }
    return (this.#fromRendererFrameMatrixCache = this.toRendererFrameMatrix.inverse());
  }

  /**
   * @returns the bounding rect of the given view in the renderer frame's
   * coordinate system.
   */
  getRendererFrameBoundingRectFor(view: View): DOMRect {
    const cachedRect = this.#boundingRectCache.get(view.uniqueId);
    if (cachedRect) {
      return cachedRect;
    }

    const viewMatrix = this._renderer?.viewMatrices[view.uniqueId]?.[0]?.[1];
    const viewDOMMatrix = viewMatrix ? Matrix.toDOMMatrix(viewMatrix) : new DOMMatrix();

    // In its local coordinate system, a view is always at (0, 0).
    const viewRect = new DOMRect(0, 0, view.position.width(), view.position.height());

    // Use the view's matrix to map its rect into the coordinate system of the canvas.
    const canvasRect = transformRect(viewDOMMatrix, viewRect);

    // Map the resulting rect into the coordinate system of the renderer frame.
    const rendererFrameRect = transformRect(this.toRendererFrameMatrix, canvasRect);

    return cacheAndReturn(this.#boundingRectCache, view.uniqueId, rendererFrameRect);
  }

  invalidateContentCaches(): void {
    this.#querySelectorAllCache.clear();
  }

  override invalidateRendererGeometryCaches(): void {
    this.#boundingRectCache.clear();
    this.#toRendererFrameMatrixCache = null;
    this.#fromRendererFrameMatrixCache = null;
    for (const webview of this.getWebViews()) {
      webview.playback.invalidateRendererGeometryCaches();
    }
  }

  // Live playback support is currently unimplemented for native mobile.
  appendTrailingEvents(_trailingEvents: RawEvent[]) {}

  openPage(page: PageDesc): void {
    this._page = page;
  }

  async waitForImagesToLoad(_maxWaitTime: number) {}

  override async setCursorOverride(cursor: RendererCursor): Promise<void> {
    // Override the cursor for native mobile content.
    const container = this.playbackContainer().elem();
    if (cursor === 'captured') {
      container.style.removeProperty('cursor');
    } else {
      container.style.setProperty('cursor', cursor);
    }

    // Override the cursor for webviews.
    await Promise.all(
      this.getWebViews().map(webView => webView.playback.setCursorOverride(cursor)),
    );
  }

  override async setInteractive(interactive: boolean): Promise<void> {
    await Promise.all(
      this.getWebViews().map(webView => webView.playback.setInteractive(interactive)),
    );
  }

  // TODO: Kick off resource loading before the raw page is done.
  // We can't do that yet, because this code depends upon raw page data.
  private async _loadResources(): Promise<void> {
    const loadPromises: Promise<void>[] = [this._ROBOTO_CSS_loaded];

    for (const rawFramesData of this._pageData.frameData) {
      const framesData = new Uint8Array(rawFramesData);
      const bb = new flatbuffers.ByteBuffer(framesData);
      const frameBundleRoot = FrameBundleRootProto.getRootAsFrameBundleRootProto(bb);
      loadPromises.push(
        this._loadBitmapsForFrameBundle(frameBundleRoot, this._pageData.resourceData),
      );
    }

    try {
      await Promise.all(loadPromises);
    } catch (e) {
      console.warn(`Unexpected exception while loading resources: ${e}`);
    }

    this._host.onResourceEvent({ kind: 'initialLoadComplete' });
  }

  async playPage(pageData: MobilePageData, options?: PlayPageOptions): Promise<PlayResult> {
    const start = performance.now();
    // TODO(@prashanth): Consider using an LD flag to turn this on, or remove.
    // this._debugSocket = new DebugSocket();

    this._pageData = pageData;
    const assetRoot = `${this._page.GcsBucketUrl}${this._page.Type}/${this._page.BuildId}/`;
    const canonicalRoot = `${this._page.GcsBucketUrl}${this._ctx.page.orgId}/`;
    this._pageData.resourceData = new Resources(
      this._pageData.rawResourceData,
      assetRoot,
      canonicalRoot,
    );

    // Notify the host if we couldn't interpret the resource map.
    if (this._pageData.resourceData.failed()) {
      this._host.onResourceEvent({ kind: 'resourceMapInvalid' });
    }

    // Kick off resource loading.
    const resourcesLoaded = this._loadResources();

    let driver: MobileDriver<any>;
    switch (this._page.Type) {
      case PageTypeEnum.PageTypeAndroid:
        driver = new AndroidDriver();
        break;
      case PageTypeEnum.PageTypeiOS:
        driver = new IOSDriver();
        break;
      case PageTypeEnum.PageTypeMobileMock:
        driver = new MobileMockDriver();
        break;
    }

    try {
      this._unpack(driver, pageData);
    } catch (e) {
      console.log('Unexpected error unpacking frame(s) ', e);
    }

    await resourcesLoaded;

    try {
      this._openPage(driver, pageData);

      if (typeof options?.initialTimeStampMs === 'number') {
        await this.playToTime(options?.initialTimeStampMs);
      }

      return {
        type: 'mobile',
        pageOffsetMs: 0,
        totalFrames: this._pageData.frames[this._pageData.frames.length - 1]?.index ?? 0,
        totalTimeMs: performance.now() - start,
      };
    } catch (e) {
      console.log('Unexpected error opening page', e);
    }
  }

  private async _loadBitmapsForFrameBundle(
    frameBundleRoot: any,
    resourceData: Resources,
  ): Promise<void> {
    const loadPromises: Promise<void>[] = [];

    let frameRoot = frameBundleRoot.lastFrame();
    while (frameRoot) {
      // Walk the bitmaps for the frame bundle to extract and load any bitmaps
      // we find.
      const bitmaps = DataUnpack.unpackFrameBitmaps(
        resourceData,
        frameRoot,
        this._page.Type == 'ios',
        frameBundleRoot.protocolVersion(),
      );

      for (const bitmap of Object.values(bitmaps)) {
        if (bitmap.loaded) {
          this._host.onResourceEvent({
            kind: 'resourceLoadStart',
            href: bitmap.url,
            destination: 'image',
          });

          loadPromises.push(
            (async () => {
              try {
                await bitmap.loaded;
              } catch (_) {
                this._host.onResourceEvent({
                  kind: 'resourceLoadFailed',
                  href: bitmap.url,
                  destination: 'image',
                });
              }
            })(),
          );
        }
      }
      // Walk the frames
      frameRoot = frameRoot.previous(frameRoot);
    }

    await Promise.all(loadPromises);
  }

  resize() {
    this.scalePlaybackFor(this._canvas.width, this._canvas.height, 1 / this._scale);
  }

  private _unpack<OP>(driver: MobileDriver<OP>, pageData: MobilePageData) {
    const webBundles = new WebBundlesBuilder();
    pageData.frames = [];
    pageData.frameData.forEach((frameRootBuffer, index) => {
      this._unpackFrames(driver, frameRootBuffer, pageData, webBundles, index);
    });
    pageData.webBundles = webBundles.freeze();

    // Remove any holes in playback data (mainly duplicate and idle frames)
    pageData.frames = pageData.frames.filter(f => f);

    // We're going to collect the time ranges when the Keyboard was open.
    // We'll use this to determine when to show the keyboard when we render each interpolation.
    this._keyboardOpenTimeline = new PiecewiseFunction<boolean>();
    for (let i = 0; i < pageData.frames.length; i++) {
      const frame = pageData.frames[i];
      for (let j = 0; j < frame.keyboardEvents.length; j++) {
        const kbe = frame.keyboardEvents[j];
        this._keyboardOpenTimeline.add(kbe.visible, kbe.timestamp, Number.POSITIVE_INFINITY);
      }
    }
  }

  private _unpackFrames<OP>(
    driver: MobileDriver<OP>,
    frameRootBuffer: ArrayBuffer,
    pageData: MobilePageData,
    webBundles: WebBundlesBuilder,
    bundleIndex: number,
  ) {
    if (!frameRootBuffer?.byteLength) {
      console.error('Unable to unpack 0-length frame root buffer');
      return;
    }
    const framesData = new Uint8Array(frameRootBuffer);
    const bb = new flatbuffers.ByteBuffer(framesData);
    const frameRoot = FrameBundleRootProto.getRootAsFrameBundleRootProto(bb);
    this._protocolVersion = frameRoot.protocolVersion();
    if (this._protocolVersion === 0) {
      console.warn(
        'Protocol version from frameRoot flatbuffer is 0; this may indicate missing or corrupted data.',
      );
    }

    const data = DataUnpack.unpack(frameRoot, driver, pageData.resourceData);
    this._lastFrameTime = Math.max(this._lastFrameTime, data.lastFrameTime);

    for (const [frameIndex, frame] of Object.entries(data.frames)) {
      frame.bundleIndex = bundleIndex;
      pageData.frames[+frameIndex] = frame;
    }
    DataUnpack.unpackWebBundles(frameRoot, webBundles, pageData.rawResourceData.type);
  }

  private _openPage(driver: MobileDriver<any>, page: MobilePageData) {
    this._nextFrameIndex = 0;
    this._renderer = new CanvasRenderer(
      driver,
      this.overlays().view().elem(),
      this._canvas,
      this.overlays(),
      this._scale,
    );
    this._pageData = page;
    this._webViews = {};

    const styles = {};

    /* Grab per-frame fonts. */
    page.frames.forEach(frame => {
      Object.keys(frame.fonts).forEach(font => {
        // Only one entry per font
        if (styles[font]) return;

        // Unexpected characters in font - could be someone poking at security
        // Some examples of the names for fonts would be:
        // sha1:3yVciY-M66ZY4GpvckPeUWZdx8w= - new SDK with sha1
        // sha256:3yVciY-M66ZY4GpvckPeUWZdx8w= - new hash algo going forward
        // 3yVciY-M66ZY4GpvckPeUWZdx8w= - hash without algo prefix - old SDKs. assume sha1
        if (font.match(/[^a-zA-Z0-9_=\-:]/)) {
          console.log(`Unexpected characters in '${font}'`);
          return;
        }

        // Unexpected characters in path - could be someone poking at security
        const path = this._pageData.resourceData.getPathForResourceByHash(font, true);
        if (path.match(/[^a-zA-Z0-9_=\-/:.]/)) {
          console.log(`Unexpected font path characters in '${path}' for '${font}'`);
          return;
        }
        styles[font] = path;
      });
    });

    /* Grab resource fonts. */
    const fonts = this._pageData.resourceData.allFonts();
    fonts.forEach(obj => {
      const font = obj.getName();
      const url = obj.getUrl();

      if (styles[font]) return;

      styles[font] = url;
    });

    Object.keys(styles).forEach(font => {
      const path = styles[font];

      const id = `font-${font.replace(/[^a-z0-9_-]+/gi, '')}`;
      if (document.getElementById(id)) {
        // Hey we already have this font
        return;
      }

      const style = document.createElement('style');
      style.id = id;
      this.view().elem().parentElement.appendChild(style);

      const ruleText =
        `@font-face { font-family: 'dynamic-${font.replace(
          /'/g,
          '',
        )}'; src: url('${path}') format('truetype');` +
        `font-weight: normal; font-style: normal; }\n`;
      (<CSSStyleSheet>style.sheet).insertRule(ruleText, 0);
    });

    // TODO(mmastrac): Start time?
    // TODO(jordanhurt): remove after the AppPlaybackFirstInsertWhenRefactor flag
    this._host.setFirstInsertWhen(0);
    this.init();
  }

  async playToTime(pageOffset: number, requestId?: number | string): Promise<SeekResult> {
    const start = performance.now();

    if (requestId !== undefined) {
      throw new Error(`The mobile renderer does not support onPlayToTimeCommitted()`);
    }

    const frames = this._pageData.frames;
    if (!frames || frames.length == 0) {
      return; // Can't interpolate with no frames.
    }

    this._renderer.scale = this._scale;

    if (frames.length == 1) {
      // Special-case the situation where we only have one actual frame. This should
      // only occur in testing scenarios.
      // Make a copy of the frame we *do* have.
      const f = new Frame();
      for (const k of Object.keys(frames[0])) {
        f[k] = frames[0][k];
      }
      // Set the duplicated frame's timestamp to the last frame timestamp returned by the dataunpack code so that the session will have a non-zero duration.
      f.timestamp = this._lastFrameTime;

      frames.push(f);
    }

    // Simple linear scan forwards and backwards - we could be way smarter here but this is surprisingly effective.
    while (
      this._nextFrameIndex < frames.length - 1 &&
      pageOffset > frames[this._nextFrameIndex].timestamp
    ) {
      this._nextFrameIndex++;
      this._curInterpolation = null;
    }

    while (this._nextFrameIndex > 1 && pageOffset < frames[this._nextFrameIndex - 1].timestamp) {
      this._nextFrameIndex--;
      this._curInterpolation = null;
    }

    // Since we're interpolating, always start on frame 1
    if (this._nextFrameIndex == 0) {
      this._nextFrameIndex++;
    }

    if (!this._curInterpolation) {
      // make sure we have enough frames to interpolate
      if (frames.length > this._nextFrameIndex) {
        this._curInterpolation = Interpolator.interpolate(
          frames[this._nextFrameIndex - 1],
          frames[this._nextFrameIndex],
        );
      }
    }

    // there's a possibility that we didn't create an interpolator - most likely because the session has started but
    // the app has not yet been backgrounded, so handle that here
    const webViews = this._pageData.webBundles.computeState(pageOffset);
    if (this._curInterpolation) {
      if (!this._renderTimer) {
        const renderer = this._renderer;
        const interpolation = this._curInterpolation;
        interpolation.setTime(pageOffset);
        const t1 = pageOffset - frames[this._nextFrameIndex - 1].timestamp;
        const t2 =
          frames[this._nextFrameIndex].timestamp - frames[this._nextFrameIndex - 1].timestamp;
        const skipped = this._skippedFrames;
        this._skippedFrames = 0;
        const keyboardOpen = this._keyboardOpenTimeline.at(pageOffset);

        this._renderTimer = setTimeout(() => {
          try {
            renderer.render(interpolation, webViews, t1, t2, skipped, keyboardOpen);
            this.resize();
            this.invalidateContentCaches();
            this.invalidateRendererGeometryCaches();
            this.onObjectsMayHaveChanged();
          } finally {
            // Allow recovery from render error
            this._renderTimer = null;
          }
        }, 0);
      } else {
        this._skippedFrames++;
      }
    }

    await this._updateIframes(webViews, frames[this._nextFrameIndex]?.density, pageOffset);

    // grab some extra bits of data for bunsen
    // note: this._curFrame is the *second* frame of the interpolation
    const frameA = frames[this._nextFrameIndex - 1];
    const frameB = frames[this._nextFrameIndex];
    const [prevFrameTs, nextFrameTs] = this._getPrevNextFrameTs(pageOffset, frames, frameA, frameB);

    // set exactFrame if appropriate
    let exactFrame = null;
    if (pageOffset === frameA.timestamp) {
      exactFrame = frameA.index;
    } else if (pageOffset === frameB.timestamp) {
      exactFrame = frameB.index;
    }

    // Note: an interpolation can cover two bundles when frameA is at the end of one and frameB is at the start of the next.
    // It's important when on an exactFrame, or when the first bundle only has single frame (often due to serializing a webview's HTML)
    const bundleFrame = pageOffset <= frameA.timestamp ? frameA : frameB;
    // Bundles are now combined into a single file per page on the backend, so in that case there will be only a single url for newer sessions
    const bundle =
      this._page.RawEventsUrls[
        this._page.RawEventsUrls.length === 1 ? 0 : bundleFrame.bundleIndex
      ] || undefined;

    return {
      type: 'mobile',
      frameA: frameA.index,
      frameB: frameB.index,
      bundle,
      prevFrameTs,
      nextFrameTs,
      exactFrame,
      pageOffsetMs: pageOffset,
      totalTimeMs: performance.now() - start,
    };
  }

  // calculate the timestamp of the previous and next frames for bunsen's frame-by-frame buttons
  private _getPrevNextFrameTs(
    pageOffset: number,
    frames: Frame[],
    frameA: Frame,
    frameB: Frame,
  ): [prevFrameTs: number, nextFrameTs: number] {
    let prevFrameTs: number, nextFrameTs: number;

    // if we're on the exact timestamp of frameA, and there's a frame before it, set prev to that frame
    if (pageOffset == frameA.timestamp && frames[this._nextFrameIndex - 2]) {
      // Note: The name "_curFrame" is misleading, because we use an interpolation between *two* frames.
      // At this point in time, we're actually rendering `this._curFrame - 1`, so we have to go back one
      // frame further to get the previous frame, hence the `- 2`.
      prevFrameTs = frames[this._nextFrameIndex - 2].timestamp;
    } else if (pageOffset > frameA.timestamp) {
      // if we're past frameA, set prev to frameA
      prevFrameTs = frameA.timestamp;
    } else {
      // there is no previous frame
      prevFrameTs = null;
    }

    // likewise for frameB and the next frame timestamp
    if (pageOffset === frameB.timestamp && frames[this._nextFrameIndex + 1]) {
      nextFrameTs = frames[this._nextFrameIndex + 1].timestamp;
    } else if (pageOffset < frameB.timestamp) {
      nextFrameTs = frameB.timestamp;
    } else {
      nextFrameTs = null;
    }

    return [prevFrameTs, nextFrameTs];
  }

  async onPause(): Promise<void> {
    for (const webView of Object.values(this._webViews)) {
      webView.playback.onPause();
    }
  }

  getPlaybackFrameHTML(): string | null {
    return null;
  }

  private async _updateIframes(
    webViews: WebViewsState,
    frameDensity: number | null,
    pageOffset: number,
  ): Promise<void> {
    // Delete any webviews that no longer exist. Don't care about iteration order
    for (const id in this._webViews) {
      if (!webViews.existsNow(id, this._webViews[id].epoch)) {
        const { container, playback } = this._webViews[id];
        playback.teardown();
        container.remove();
        delete this._webViews[id];
      }
    }

    // Create any new webviews that we need
    // TODO(mmastrac): We should handle null frames elsewhere
    if (frameDensity !== null && this._renderer.viewMatrices) {
      const viewEntries = Object.entries(webViews.views);
      const seekOperations = viewEntries.map(async ([id, v]) => {
        if (!this._webViews[id]) {
          const vm = this._renderer.viewMatrices[id];
          // if a webview is blocked and it's alpha is <= 0, we still
          // want to capture that the view exists, but is invisible. we
          // will wait until the matrix exists (that's the [1]).
          if (vm && vm[0][1]) {
            const density = this._renderer.computeWebViewInitialPixelFactor(frameDensity);
            this._webViews[id] = this._createWebView(id, v, density);
          } else {
            // It's possible this view isn't ready just yet
            return;
          }
        }

        const webView = this._webViews[id];

        await webView?.playback?.playToTime(pageOffset);
      });

      await Promise.allSettled(seekOperations);
    }
  }

  private _createWebView(id: ObjectId, state: WebBundle, density: number): WebView {
    const container = document.createElement('div');
    container.setAttribute('class', 'web-view-container');
    container.setAttribute('inner-playback-id', id);
    container.style.position = 'absolute';
    container.style.top = '0';
    container.style.left = '0';
    container.style.transformOrigin = '0 0';
    container.style.borderWidth = '0';

    // Initial bounds match the current view's bounds, guessing xform using current density
    const transform = this._renderer.viewMatrices[id];
    Matrix.setCssMatrix(
      Matrix.multiply(Matrix.scale(density, density), transform[0][1]),
      container.style,
    );
    container.style.width = `${transform[0][0].position.width() / density}px`;
    container.style.height = `${transform[0][0].position.height() / density}px`;
    // todo(Nathan): use the actual z-index of the webviews so that older ones can appear on top of newer ones if that's how an app displays it
    // DOM order is webviews (oldest first), then the canvas
    this.playbackContainer().e.insertBefore(container, this._canvas);

    const host: FramePlaybackHost = {
      onEvent: this.onEvent,
      onResourceLoadStart: (href: string, destination: RequestDestination) => {
        this._host.onResourceEvent({
          kind: 'resourceLoadStart',
          href,
          destination,
        });
      },
      onResourceLoaded: (href: string, destination: RequestDestination, result: LoadResult) => {
        if (result === 'error') {
          this._host.onResourceEvent({
            kind: 'resourceLoadFailed',
            href,
            destination,
          });
        }
      },
      positionPointer: (_x: number, _y: number) => {},
      positionTouch: (_touchid: number, _x: number, _y: number) => {},
      touchDown: (_touchid: number, _x: number, _y: number) => {},
      touchUp: (_touchid: number) => {},
      removeAllTouches: () => {},
      cursor: (_cursorClass: string) => {},
      show: () => {},
      hide: () => {},
      setUserFocus: (_point: Record<'x' | 'y', number>) => {},
      updateRootFrameSize: (
        { width, height }: Record<'width' | 'height', number>,
        _visualViewport: OffsetRect,
      ) => {
        if (width <= 0 || height <= 0) {
          logWarnOnce('Ignoring zero-sized updateRootFrameSize w=', width, 'h=', height);
          return;
        }
        const transform = this._renderer.viewMatrices[id];
        if (transform && transform[0]) {
          const view = transform[0][0];
          const xform = transform[0][1];
          // TODO(mmastrac): This seems too simplistic to work in the general transform case - revisit
          const scale = Matrix.scale(
            view.position.width() / width,
            view.position.height() / height,
          );
          Matrix.setCssMatrix(Matrix.multiply(scale, xform), container.style);
          container.style.width = `${width}px`;
          container.style.height = `${height}px`;
        }
      },
      playToTimeCommitted: (_requestId: number | string) => {
        // TODO: The mobile renderer does not support onPlayToTimeCommitted().
      },
      onObjectsMayHaveChanged: this.onObjectsMayHaveChanged,
    };

    const framePlayback = new FramePlayback({
      ctx: this._ctx,
      container,
      host,
      preloader: this._preloader,
      orgId: this._ctx.page.orgId,
      windowSize: this._windowSize,
      screenSize: this._screenSize,
    });

    framePlayback.appendRawEvents(state.events);

    const inspectorContext = {
      activeRootDocFrame: () => framePlayback.activeRootDocFrame(),
      activeFsRootDocument: () => framePlayback.activeFsRootDocument(),
    };

    const inspectorHost = CreateClient(
      this._dispatcher.Ch(),
      new InspectorHostClientStub(),
      'InspectorHost',
    );

    const inspector = new WebInspector(inspectorContext, inspectorHost);

    return {
      container,
      epoch: state.epoch,
      playback: framePlayback,
      inspector,
    };
  }

  private init() {
    // Hide this until we've played at least one frame
  }

  protected beforeEnteringPageInsights() {}

  protected afterLeavingPageInsights() {}
}
